import { useRef, useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { cn } from 'utils/helpers';
import { ITimeSlot } from 'modules/profile/components/availability/TimeSlot/TimeSlot';
import { ReactComponent as ClockIcon } from 'assets/images/profile/clock-icon.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

type TimeDropdownProps = {
  selectedTime?: string;
  onTimeSelect: (time: ITimeSlot) => void;
  className?: string;
  error?: boolean;
  startTime?: string;
  disabled?: boolean;
  useDefaultTime?: boolean;
  showSelectedInitially?: boolean;
};

interface TimeOption {
  value: string; // 24-hour format (HH:MM)
  label: string; // 12-hour format (H:MMAM/PM)
  icalValue: string; // Format like T000000 for midnight
}

const AvailabilityTimeSelector: React.FC<TimeDropdownProps> = (props) => {
  const [selectedValue, setSelectedValue] = useState<any>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Generate all time options in 15-minute increments
  const timeOptions = useMemo(() => {
    const options: TimeOption[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 15, 30, 45]) {
        const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const dayJsTime = dayjs(`2000-01-01T${time24}`);

        // Format for display (12-hour format)
        const label = dayJsTime.format('h:mmA');

        // Format for iCal (T000000 format)
        const hourMinStr =
          String(hour).padStart(2, '0') +
          String(minute).padStart(2, '0') +
          '00';
        const icalValue = `T${hourMinStr}`;

        options.push({
          value: time24,
          label,
          icalValue,
        });
      }
    }
    return options;
  }, []);

  const selectedValueIn12Hours = selectedValue
    ? selectedValue.label
    : props.selectedTime
      ? props.selectedTime
      : undefined;

  const getTime = (time: string, format?: string): string[] => {
    const today = dayjs().format('YYYY-MM-DD');
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return [dayjs.tz(`${today}T${time}`, timeZone).format(format), timeZone];
  };

  const handleInputClick = () => {
    if (!props.disabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionSelect = (option: TimeOption) => {
    const [dateTime, timeZone] = getTime(option.value);
    setSelectedValue({
      dateTime,
      timeZone,
      label: option.label,
      value: option.value,
    });
    setIsDropdownOpen(false);
    props.onTimeSelect({ dateTime, timeZone });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Block scrolling when dropdown is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    if (isDropdownOpen) {
      // Get width of scrollbar to prevent layout shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // Prevent body scrolling
      document.body.style.overflow = 'hidden';

      // Add padding right to compensate for scrollbar disappearance
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      // Restore original styles when component unmounts or dropdown closes
      if (isDropdownOpen) {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      }
    };
  }, [isDropdownOpen]);

  return (
    <div
      className={cn(
        'relative w-full font-sans text-sm font-medium text-gray-400 border rounded-full bg-[#ABE49C] border-transparent overflow-visible cursor-pointer',
        props.className,
        props.error && 'border-red-500',
        props.disabled && 'opacity-50',
      )}
    >
      <div
        className="flex items-center justify-center relative"
        onClick={handleInputClick}
      >
        <div className={`flex justify-center items-center w-full relative`}>
          <span
            className={`absolute mx-auto bottom-[50%] translate-y-[50%] ${selectedValue || props.selectedTime ? 'text-black' : 'text-[#32395E]'} pointer-events-none text-nowrap whitespace-nowrap break-keep pl-5`}
          >
            {selectedValueIn12Hours ?? 'HH:MM'}
          </span>
          <span className="absolute left-[17%] bottom-[50%] translate-y-[50%]">
            <ClockIcon className="fill-[#000000] text-black" />
          </span>
          <input
            ref={inputRef}
            type="text"
            readOnly
            className="z-[-10] text-center py-3 rounded-full focus:outline-none bg-[#ABE49C] text-transparent w-full select-none cursor-pointer border-none"
            value="" // We don't show the actual value in the input, it's displayed in the span above
          />
        </div>
      </div>

      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-auto"
          style={{ marginTop: '8px' }}
        >
          <div className="w-full" role="listbox">
            {timeOptions.map((option) => (
              <div
                key={option.value}
                role="option"
                className={cn(
                  'px-4 py-2 cursor-pointer hover:bg-gray-100 text-center',
                  selectedValue && selectedValue.value === option.value
                    ? 'bg-gray-100'
                    : '',
                )}
                onClick={() => handleOptionSelect(option)}
                aria-selected={
                  selectedValue && selectedValue.value === option.value
                }
                data-ical={option.icalValue}
              >
                <span className="inline-block min-w-16 text-center">
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityTimeSelector;
